/**
 * Qué secciones de Configuración ve cada quien. Era dos `.filter(...)` inline casi iguales
 * en `Settings.tsx` (mobile y desktop); se unifican para poder sumar el gate de dueño
 * (spec 2026-09-11-invitaciones-equipo §4.3) y probarlo sin DOM.
 *
 * Dos ejes, separados a propósito desde
 * ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago:
 *
 *   • **Acceso** (`seccionPermitida`) — si a esta cuenta le corresponde la sección. No
 *     depende del viewport, y es lo que tiene que preguntar TAMBIÉN el deep-link
 *     `?seccion=…`: filtrar la lista del menú y dejar la URL abierta es esconder el
 *     ítem, no bloquearlo.
 *   • **Ubicación** (`filtrarSecciones`) — si en ESTE viewport la sección se dibuja como
 *     ítem de la lista. "Suscripción" en mobile vive en una card arriba, no en la lista.
 */
export interface SeccionConfig {
  id: string;
  desktopOnly?: boolean;
  mobileOnly?: boolean;
  arOnly?: boolean;
}

/** Lo que se sabe de la cuenta, sin nada del viewport. */
export interface ContextoDeAcceso {
  isArgentina: boolean;
  devTester: boolean;
  esDueno: boolean;
  facturacionEnabled: boolean;
  /**
   * ¿La cuenta tiene plan PAGO? Es lo que abre "Funciones avanzadas"
   * (ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago).
   *
   * Plan desconocido cuenta como gratis, igual que el resto de estos gates: que a un
   * pago le aparezca un instante después es mejor que un parpadeo en una cuenta gratis.
   */
  planPago: boolean;
}

/**
 * ¿Esta cuenta tiene acceso a la sección? Sin mirar el viewport.
 *
 * Lo usan el menú (vía `filtrarSecciones`) y el deep-link, para que no puedan discrepar:
 * una sección escondida del menú pero alcanzable por URL no está bloqueada.
 */
export function seccionPermitida(seccion: SeccionConfig, ctx: ContextoDeAcceso): boolean {
  if (seccion.arOnly && !ctx.isArgentina) return false;
  if (seccion.id === 'facturacionPlan' && !(ctx.facturacionEnabled || ctx.devTester))
    return false;
  // "Funciones avanzadas": de `devTester` a PLAN PAGO —
  // ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago. Estuvo sólo para dev
  // mientras Pre-lanzamientos y Verificación de calidad estaban en prueba; salieron, y lo
  // que queda es el corte de plan. `devTester` se conserva para poder verla con una
  // cuenta de prueba sin plan.
  if (seccion.id === 'funcionesAvanzadas' && !(ctx.planPago || ctx.devTester))
    return false;
  // Los candados del dueño: el miembro no toca el plan ni las facturas.
  if ((seccion.id === 'plan' || seccion.id === 'facturacionPlan') && !ctx.esDueno)
    return false;
  return true;
}

export function filtrarSecciones<T extends SeccionConfig>(
  secciones: T[],
  ctx: ContextoDeAcceso & { vista: 'mobile' | 'desktop' },
): T[] {
  return secciones.filter((s) => {
    // Mobile tiene la card del plan arriba de la lista: "plan" no se repite como ítem.
    if (ctx.vista === 'mobile' && (s.id === 'plan' || s.desktopOnly)) return false;
    if (ctx.vista === 'desktop' && s.mobileOnly) return false;
    return seccionPermitida(s, ctx);
  });
}
